<template>
  <div class="source-content panels">
    <div class="filter-bar">
      <search-filter
        :value="search"
        :options="filteredTags"
        :selected="selectedTags"
        data-test="producer-assets-displayElements-searchFilter"
        @input="changeInteractiveSearch"
        @update:selected="changeInteractiveTags"
      />
    </div>

    <!-- No panels after searching -->
    <div v-if="!isLoading && !total" class="text-center">
      <h3 class="margin-bottom-md">
        No panels found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em"
        data-entity="producer-assets-resetPanelSearch"
        data-test="producer-assets-displayElements-resetSearch"
        @click="clearTags"
      >
        Reset Search
      </button>
    </div>

    <div
      ref="sourceList"
      class="source-list"
      data-test="producer-assets-displayElements-list"
    >
      <draggable
        :list="filteredPanels"
        @start="startDrag"
        @end="$store.dispatch('clearDragType')"
        :clone="clone"
        :sort="false"
        :animation="200"
        revert-on-spill="true"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
      >
        <!-- Need both of the below classes for draggable logic in TimelineTheme -->
        <div
          v-for="panel in filteredPanels"
          :key="panel._id"
          class="source-item smart-panel"
          :data-template-id="panel._id"
          data-entity="producer-assets-panel"
          :data-test="`producer-assets-element:${panel._id}`"
        >
          <div class="tb-checkbox">
            <input
              type="checkbox"
              :id="panel._id"
              :value="panel"
              v-model="selected"
              data-entity="producer-assets-togglePanelSelection"
              :data-test="`producer-assets-element-checkBox:${panel._id}`"
            >

            <label class="checkbox" :for="panel._id" />
          </div>

          <div class="source-item-wrapper">
            <img class="preview margin-right-xs" :src="panel.preview_url"
              :data-test="`producer-assets-element-preview:${panel._id}`" />

            <div class="source-item-info-wrapper">
              <div class="source-title title-text" :title="panel.title"
                :data-test="`producer-assets-element-title:${panel._id}`">
                {{ panel.title }}
              </div>

              <div
                :class="{
                  'source-item-tags': true,
                  'margin-top-xxs': panel.tags?.length,
                }"
                :data-test="`producer-assets-element-tags:${panel._id}`"
              >
                <span
                  v-for="tag in getUniqueTags(panel.tags)"
                  :key="tag"
                  class="tag"
                  :data-test="`producer-assets-element-tags:${panel._id}_${tag}`"
                >
                  {{ tag }}
                </span>
              </div>
            </div>

            <div class="source-description margin-left-xxs">
              <div class="description-text capitalize"
                :data-test="`producer-assets-element-type:${panel._id}`">
                {{ displayTitle(panel) }}
              </div>
            </div>

            <div class="source-actions margin-left-xxs">
              <button
                class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                title="Add to playlist"
                data-entity="producer-assets-addPanelToPlaylist"
                :data-test="`producer-assets-element-addToPlaylist:${panel._id}`"
                @click="addToPlaylist(panel, 'Panel')"
              >
                <span class="icon icon-plus" />
              </button>

              <!-- CAN WE REMOVE THIS CONTEXT MENU? WHY ARE PANELS SO SPECIAL? -->
              <dropdown position="left" class="more-actions margin-left-xxs">
                <template slot="trigger">
                  <button
                    class="tb-btn tb-btn-text sm medium-em"
                    title="More actions"
                    data-entity="producer-assets-togglePanelContextMenu"
                    :data-test="`producer-assets-element-context:${panel._id}`"
                  >
                    <span class="icon icon-more-vertical" />
                  </button>
                </template>

                <template slot="dropdown-body">
                  <button
                    v-if="panel.type !== 'widget'"
                    class="dropdown-list-item"
                    data-entity="producer-assets-editPanel"
                    @click="openEditModal(panel)"
                  >
                    <span class="dropdown-list-item-label">Edit</span>
                  </button>

                  <button
                    v-if="panel.type !== 'widget'"
                    class="dropdown-list-item"
                    data-entity="producer-assets-duplicatePanel"
                    @click="duplicatePanel(panel._id)"
                  >
                    <span class="dropdown-list-item-label">Duplicate</span>
                  </button>
                </template>
              </dropdown>
            </div>
          </div>
        </div>
      </draggable>

      <div ref="loadTrigger" class="load-trigger">
        <div v-if="isLoading" class="loading loading--inline" data-test="producer-assets-displayElements-loading">
          <i class="icon icon-spinner"></i>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="flex content-end items-center padding-md"
      data-test="producer-assets-selected"
    >
      <p class="text-primary margin-bottom-none margin-right-sm" data-test="producer-assets-selected-count">{{ selected.length }} selected</p>

      <button
        class="tb-btn tb-btn-contained medium-em margin-left-md"
        :class="{'disabled': !selected.length }"
        :disabled="!selected.length"
        data-entity="producer-assets-addSelectedPanelsToPlaylist"
        data-test="producer-assets-selected-addToPlaylist"
        @click="addSelectedToPlaylist('Panels')"
      >
        Add to playlist
      </button>
    </div>


    <modal v-if="showPanelEditor" class="edit-smart-panel-modal">
      <template slot="modal-header">
        <div class="title">Edit Smart Panel</div>
        <button
          class="modal-close tb-btn tb-btn-text sm low-em"
          data-entity="producer-assets-closePanelEditor"
          data-test="producer-assets-closeInteractiveEditor"
          @click="showPanelEditor = false"
        >
          <span class="icon icon-cross" />
        </button>
      </template>

      <template slot="modal-body">
        <iframe border="0" width="100%" height="100%" v-bind:src="iframeSrc" />
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { pick } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import tabMixins from '../../mixins/tab-mixins';
import ModalComponent from '../../components/modal/Modal.vue';

const { v1: uuidv1 } = require('uuid'); // Dis da new way

export default {
  mixins: [tabMixins],
  components: {
    modal: ModalComponent,
    SearchFilter,
  },

  data() {
    return {
      showAlert: false,
      showPanelEditor: false,
      selectedPanel: null,
      selectedTags: [],
    };
  },

  created() {
    window.addEventListener('message', this.onMessage, false);
    this.$store.dispatch('getPanels');
    this.getInteractiveTags();
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    panels() {
      if (!this.$refs.sourceList || !this.panels.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  beforeDestroy() {
    window.removeEventListener('message', this.onMessage, false);
  },

  computed: {
    ...mapState({
      tags: (state) => state.panels.tags,
      search: (state) => state.panels.search,
      panels: (state) => state.panels.panels,
      isLoading: (state) => state.panels.isLoading,
      owner: (state) => state.production.production.owner,
      total: (state) => state.panels.total,
    }),

    ...mapGetters(['sortedInteractiveTags']),

    iframeSrc() {
      return `${process.env.TAGBOARD_PANELS_URL}/settings?user_id=${this.owner}&src=producer#/${this.selectedPanel._id}/step/general`;
    },
    allValidPanels() {
      const excludedTypes = ['leaderboard', 'hashtag-meter', 'youtube', 'vimeo', 'hashtag-battle', 'cta'];
      return this.panels.filter(panel => !excludedTypes.includes(panel.type));
    },
    filteredPanels() {
      return this.allValidPanels.filter(panel => {
        if (this.selectedTags.length) {
          const panelTags = (panel.tags || []).map(t => t.toLowerCase());
          return this.selectedTags.some(tag => panelTags.includes(tag));
        }

        return true;
      });
    },
    filteredTags() {
      const tagSet = new Set();

      this.allValidPanels.forEach(panel => {
        (panel.tags || []).forEach(tag => tagSet.add(tag.toLowerCase()));
      });

      return Array.from(tagSet).sort();
    },
  },

  methods: {
    ...mapActions([
      'getInteractiveTags',
      'changeInteractiveSearch',
    ]),
    changeInteractiveTags(tags) {
      this.selectedTags = tags.map(t => t.toLowerCase());
    },
    clearTags() {
      this.selectedTags = [];
    },
    getUniqueTags(tags = []) {
      return [...new Set(tags.map((t) => t.toLowerCase()))];
    },

    /**
    * Attempt to load more smart panels
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMoreSmartPanels) {
        this.$store.dispatch('incrementPanelsPage');
      }
    },

    onMessage(e) {
      if (e.origin !== process.env.TAGBOARD_PANELS_URL) {
        return;
      }

      if (typeof e.data === 'string' && e.data.indexOf('tgbSmartPanelUpdate') === 0) {
        const panel = JSON.parse(e.data.split('|')[1]);
        this.$store.commit('EDIT_PANEL', panel);
      }
    },

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === templateId);
      this.$store.dispatch('setDragType', {
        type: 'panel',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    displayTitle(panel) {
      // Allow pretty names for multi worded panels types
      switch (panel.type) {
        case 'hashtag-battle':
          return 'hashtag poll';
        case 'twitter-poll':
          return 'twitter poll';
        case 'reddit-poll':
          return 'reddit poll';
        case 'multiple-choice-poll':
          return 'Poll';
        case 'poll-results':
          return 'Poll Results';
        default:
          return panel.type;
      }
    },

    openEditModal(panel) {
      this.selectedPanel = panel;
      this.showPanelEditor = true;
    },

    duplicatePanel(panelId) {
      this.$store.dispatch('duplicatePanel', panelId);
    },

    clone(original) {
      const clone = pick(original, ['_id', 'type', 'title']);
      clone.timeline_id = uuidv1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.layout = 'panel';
      clone.posts = [{ timeline_id: clone.timeline_id }];
      return clone;
    },
  },
};
</script>
